import { formatDate } from "date-fns/format";
import { PropsWithChildren, useState } from "react";
import { Spinner } from "@radix-ui/themes";
import { cn } from "@/core/helpers/cn";
import { imgAvatar } from "@/shared/assets/img";
import { usePlayerQuery } from "@/shared/hooks/useGetPlayer";
import { EyeIcon } from "@/shared/icons/EyeIcon";
import Switcher from "@/shared/ui/Switcher";
import { useBankCharacterQuery } from "@/domains/bank/hooks/useBankCharacterQuery";
import { BankBranchCardInfoContainer } from "@/domains/bank/containers/branch/BankBranchCardInfoContainer";
import { BankBranchDepositFormContainer } from "@/domains/bank/containers/branch/BankBranchDepositFormContainer";
import { BankBranchTransferFormContainer } from "@/domains/bank/containers/branch/BankBranchTransferFormContainer";
import { BankBranchWithdrawFormContainer } from "@/domains/bank/containers/branch/BankBranchWithdrawFormContainer";
import { BankBranchCardBalance } from "@/domains/bank/components/BankBranch/components/BankBranchCardBalance";
import { BankBranchDrawer } from "@/domains/bank/components/BankBranch/components/BankBranchDrawer";
import { BankCardIcon } from "@/domains/bank/components/Icons/BankCardIcon";
import { DepositIcon } from "@/domains/bank/components/Icons/DepositIcon";
import { SmartphoneIcon } from "@/domains/bank/components/Icons/SmartphoneIcon";
import { WithdrawIcon } from "@/domains/bank/components/Icons/WithdrawIcon";

function NavCard({
  title,
  description,
  className,
  onClick,
  Icon,
}: {
  title: string;
  description: string;
  className?: string;
  onClick?: () => void;
  Icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative group rounded-[12px] p-6 px-7 bg-dark-800 bg-gradient-to-r from-[#1c1c1c] from-0% via-dark-800 via-50%",
        className,
      )}
    >
      <div className="absolute inset-0 rounded-[12px] overflow-hidden transition-opacity group-hover:duration-0 duration-200 opacity-0 group-hover:opacity-30 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-[#21a03880] from-0% via-[#21a03880] via-50% to-[#21a038df] to-100%" />
      </div>

      <div className="text-left">
        <h4 className="text-white text-16 font-medium">{title}</h4>
        <p className="text-white/50 text-14 font-normal group-hover:text-sbank">{description}</p>
      </div>

      <div className="absolute right-5 top-5 size-10">
        <Icon className="absolute z-1 top-0 right-0" />
        <Icon className="absolute z-0 top-3 right-2 blur-[1px] opacity-15 " />
      </div>
    </button>
  );
}

function Title({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn("font-bold leading-none uppercase text-white/50 !text-12", className)}>
      {children}
    </div>
  );
}

export function BankBranchHomeTabContainer({ branchId }: { branchId: number }) {
  const [hideBalance, setHideBalance] = useState(false);
  const bankCharacterQuery = useBankCharacterQuery({ enabled: true });
  const playerQuery = usePlayerQuery();

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isPhoneTopUpOpen, setIsPhoneTopUpOpen] = useState(false);
  const [isUnderConstructionOpen, setIsUnderConstructionOpen] = useState(false);

  const name = playerQuery.data?.character?.name.content;
  const id = playerQuery.data?.character?.id;
  const cardBalance = bankCharacterQuery.data?.balance;
  const cashBalance = bankCharacterQuery.data?.cash;

  if (playerQuery.isLoading || bankCharacterQuery.isLoading) {
    return <Spinner className="size-5 animate-spin" />;
  }

  return (
    <>
      <div className="grid grid-cols-[360px_1fr] gap-7 h-full">
        <BankBranchCardInfoContainer />

        <div className="flex flex-col justify-between h-full pb-2">
          <div className="relative group rounded-[12px] p-5 bg-dark-800 bg-gradient-to-r from-[#1c1c1c] from-0% via-dark-800 via-50% flex items-center h-[84px] justify-between">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div
                className="size-12 rounded-2xl bg-white/10"
                style={{
                  backgroundImage: `url(${imgAvatar})`,
                  backgroundSize: "cover",
                }}
              />

              <div className="text-left">
                <div className="text-12 font-bold uppercase">{name}</div>
                <div className="text-12 font-bold text-white/50">ID: {id}</div>
              </div>
            </div>

            <BankBranchCardBalance
              hideBalance={hideBalance}
              balance={Number(cardBalance)}
              className="text-right"
            />
          </div>

          <div>
            <Title className="mb-4">Банковские операции</Title>
            <div className="grid grid-cols-2 gap-4">
              <NavCard
                title="Снять"
                description="Получение наличных средств"
                onClick={() => {
                  console.log("withdraw");
                  setIsWithdrawOpen(true);
                }}
                Icon={WithdrawIcon}
              />

              <NavCard
                title="Внести"
                description="Внесение наличных средств"
                onClick={() => setIsDepositOpen(true)}
                Icon={DepositIcon}
              />
            </div>
          </div>

          <div>
            <Title className="mb-4">Денежные переводы</Title>
            <div className="grid grid-cols-2 gap-4">
              <NavCard
                title="По номеру счета"
                description="Перевод средств игроку"
                onClick={() => setIsTransferOpen(true)}
                Icon={BankCardIcon}
              />
              <NavCard
                title="По номеру телефона"
                description="Перевод средств игроку"
                onClick={() => setIsUnderConstructionOpen(true)}
                Icon={SmartphoneIcon}
              />
            </div>
          </div>

          <div>
            <Title className="mb-4">Прочее</Title>
            <div className="grid grid-cols-2 gap-4">
              <NavCard
                title="Пополнить телефон"
                description="Оплата мобильной связи"
                onClick={() => setIsPhoneTopUpOpen(true)}
                Icon={SmartphoneIcon}
              />
            </div>
          </div>

          {/* Footer */}
          <footer className="flex items-center justify-between text-white/50 text-14 font-normal">
            <div>
              Расчетный день: {formatDate(bankCharacterQuery.data?.nextTariffPayment, "dd.MM.yyyy")}
            </div>

            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setHideBalance((value) => !value);
                }
              }}
              className="flex items-center gap-4 hover:bg-white/5 rounded-lg py-2 px-3"
              onClick={() => setHideBalance((value) => !value)}
            >
              <div className="flex items-center gap-2">
                <EyeIcon className="size-4 opacity-40" />
                <div className="leading-none">Показывать баланс счета</div>
              </div>

              <Switcher
                id="show-balance"
                on={!hideBalance}
                onChange={() => setHideBalance(!hideBalance)}
              />
            </div>
          </footer>
        </div>
      </div>

      <BankBranchDrawer
        title="Снятие наличных"
        isOpen={isWithdrawOpen}
        onClose={() => setIsWithdrawOpen(false)}
      >
        <BankBranchWithdrawFormContainer
          key={String(isWithdrawOpen)}
          cardBalance={cardBalance}
          cashBalance={cashBalance}
          branchId={branchId}
          onCancel={() => setIsWithdrawOpen(false)}
        />
      </BankBranchDrawer>

      <BankBranchDrawer
        title="Внесение наличных"
        isOpen={isDepositOpen}
        onClose={() => setIsDepositOpen(false)}
      >
        <BankBranchDepositFormContainer
          key={String(isDepositOpen)}
          cardBalance={cardBalance}
          cashBalance={cashBalance}
          branchId={branchId}
          onCancel={() => setIsDepositOpen(false)}
        />
      </BankBranchDrawer>

      <BankBranchDrawer
        title="Пополнить телефон"
        isOpen={isPhoneTopUpOpen}
        onClose={() => setIsPhoneTopUpOpen(false)}
      >
        <div>Пополнение телефона временно недоступно</div>
      </BankBranchDrawer>

      <BankBranchDrawer
        title="Перевести"
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
      >
        <BankBranchTransferFormContainer
          key={String(isTransferOpen)}
          cardBalance={cardBalance}
          branchId={branchId}
          onCancel={() => setIsTransferOpen(false)}
        />
      </BankBranchDrawer>

      <BankBranchDrawer
        title="Раздел находится в разработке"
        isOpen={isUnderConstructionOpen}
        onClose={() => setIsUnderConstructionOpen(false)}
      >
        <div>Раздел находится в разработке</div>
      </BankBranchDrawer>
    </>
  );
}
